import React from "react";
import { Plus } from "lucide-react";

import { ListCard, ListTile } from "@/shared/components/ListCard";
import { controlCls } from "@/shared/components/teams-ui";
import { Switch } from "@/shared/components/ui/switch";
import { MAX_QUESTIONS, QUICK_QUESTIONS, STEPS } from "../blocks";
import { newQuestion, type DraftQuestions, type DraftStep } from "../steps";
import { QuestionRow } from "./QuestionRow";
import { STEP_ICONS } from "./StepCard";

/* The open step, beside the canvas: a questions step's screen title and its questions —
   each with its type, whether it's required and what the type takes — with the questions
   most flows ask one press away; a sign-in or phone step says what it asks of people. The
   eye shows the screen as people see it. */
export function StepInspector({ step, index, onChange, onRemove }: {
  step: DraftStep | null;
  index: number;
  onChange: (step: DraftStep) => void;
  onRemove: () => void;
}) {
  return (
    <ListCard className="flex-[1_1_360px] min-w-0">
      {!step ? (
        <p className="m-0 px-2 py-8 text-center text-[12.5px] leading-[1.55] text-[var(--text-tertiary)]">
          Pick a step to edit it, or press + in the flow to add one.
        </p>
      ) : (
        <section aria-label="Step" className="p-4 flex flex-col gap-3.5">
          <Header step={step} index={index} onRemove={onRemove} />
          {step.type === "questions" ? (
            <Questions step={step} onChange={onChange} />
          ) : (
            <>
              <p className="m-0 text-[12.5px] leading-[1.6] text-[var(--text-secondary)]">{STEPS[step.type].says}</p>
              {step.type === "phone" && (
                <label className="flex items-center gap-3 text-[13px] text-[var(--text-primary)]">
                  <span className="flex-1">One person per number</span>
                  <Switch
                    aria-label="One person per number"
                    checked={!!step.unique}
                    onCheckedChange={(on) => onChange({ ...step, unique: on })}
                  />
                </label>
              )}
            </>
          )}
        </section>
      )}
    </ListCard>
  );
}

function Header({ step, index, onRemove }: {
  step: DraftStep;
  index: number;
  onRemove: () => void;
}) {
  const Icon = STEP_ICONS[step.type];
  return (
    <div className="flex items-center gap-2 min-w-0">
      <ListTile className="w-7 h-7">
        <Icon className="w-[14px] h-[14px]" strokeWidth={1.75} />
      </ListTile>
      <span className="min-w-0 flex-1">
        <span className="block text-[11.5px] text-[var(--text-tertiary)]">Step {index + 1}</span>
        <span className="block text-[14px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
          {step.type === "questions" ? "Questions" : STEPS[step.type].name}
        </span>
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="press h-7 px-2.5 rounded-[9px] text-[12.5px] font-medium whitespace-nowrap text-[var(--danger)] hover:bg-[var(--danger-bg)]"
      >
        Remove step
      </button>
    </div>
  );
}

function Questions({ step, onChange }: { step: DraftQuestions; onChange: (step: DraftQuestions) => void }) {
  const fields = step.fields;
  const full = fields.length >= MAX_QUESTIONS;
  const asked = new Set(fields.map((q) => q.label.trim().toLowerCase()));
  const setFields = (next: DraftQuestions["fields"]) => onChange({ ...step, fields: next });

  return (
    <>
      <label className="flex flex-col gap-1.5">
        <span className="text-[12px] font-medium text-[var(--text-secondary)]">Screen title</span>
        <input
          value={step.title ?? ""}
          maxLength={100}
          placeholder="Optional, e.g. About your team"
          onChange={(e) => onChange({ ...step, title: e.target.value || null })}
          className={`${controlCls} w-full`}
        />
      </label>
      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-medium text-[var(--text-secondary)]">
          Questions <span className="font-mono tabular-nums text-[var(--text-quaternary)]">{fields.length}</span>
        </span>
        {fields.map((question, i) => (
          <QuestionRow
            key={question.uid}
            question={question}
            number={i + 1}
            onChange={(next) => setFields(fields.map((q, j) => (j === i ? { ...next, uid: q.uid } : q)))}
            onRemove={() => setFields(fields.filter((_, j) => j !== i))}
          />
        ))}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={full}
            onClick={() => setFields([...fields, newQuestion()])}
            className="press h-7 px-2.5 rounded-[8px] flex items-center gap-1 text-[12px] font-medium text-[var(--text-primary)] ring-1 ring-[var(--hairline-strong)] hover:bg-[var(--accent-hover)] disabled:opacity-40 disabled:pointer-events-none"
          >
            <Plus className="w-3 h-3" strokeWidth={2} /> Question
          </button>
          {QUICK_QUESTIONS.filter((q) => !asked.has(q.label.toLowerCase())).map((quick) => (
            <button
              key={quick.label}
              type="button"
              disabled={full}
              aria-label={`Add ${quick.label}`}
              onClick={() =>
                setFields([...fields.filter((q) => q.label.trim()), newQuestion({ ...quick, required: false })])
              }
              className="press h-7 px-2.5 rounded-[8px] flex items-center gap-1 text-[12px] text-[var(--text-secondary)] bg-[var(--surface-2)] hover:bg-[var(--surface-3)] hover:text-[var(--text-primary)] disabled:opacity-40 disabled:pointer-events-none"
            >
              <Plus className="w-[11px] h-[11px]" strokeWidth={2} /> {quick.label}
            </button>
          ))}
        </div>
        <p className="m-0 text-[11.5px] leading-[1.6] text-[var(--text-tertiary)]">
          Every answer is checked against its type before it's kept. Change a question later and people are asked it again.
        </p>
      </div>
    </>
  );
}
